/**
 * Glue between a finished match and the save file: daily bests, career
 * progression, unlocks and lifetime stats. Pure functions over SaveData.
 */
import type { CameraKind, GradeReport, ResultSummary } from '../core/contracts';
import { dailyNumber, recordDaily } from './daily';
import { nextCareerMatch, recordCareerResult, type CareerOutcome, type Difficulty, type TierId } from './career';
import { grantUnlocks, type GraphicsPackageId, type UnlockId } from './unlocks';
import type { SaveData } from './storage';
import type { PunchMode } from './settings';

export type MatchMode = 'daily' | 'career' | 'quick' | 'tutorial';

/** Everything the game needs to know about how a match was started (passed to onStartMatch). */
export interface StartMatchOptions {
  mode: MatchMode;
  difficulty: Difficulty;
  halfLengthSeconds: number;
  /** Starting audience for the viewers model. */
  baseAudience: number;
  unlockedCameras: CameraKind[];
  graphicsPackage: GraphicsPackageId;
  punchMode: PunchMode;
  daily?: { dateKey: string; number: number };
  career?: { tierId: TierId; tierIndex: number; matchIndex: number; matchNumber: number; matchesInTier: number };
}

/** Extra numbers the console observed during the match (not in GradeReport). */
export interface MatchExtras {
  peakViewers?: number;
  bestStreak?: number;
  viewerHistory?: number[];
}

export interface RecordOutcome {
  isNewDailyBest: boolean;
  career?: { outcome: CareerOutcome; average: number; tierName: string; nextTierName?: string };
  granted: UnlockId[];
}

type KeyMoment = GradeReport['keyMoments'][number] & { type?: string };

/** Goal key moments: uses `type` if the grader provides it, otherwise label sniffing. */
export function goalMoments(report: GradeReport): KeyMoment[] {
  return (report.keyMoments as KeyMoment[]).filter((k) =>
    k.type ? k.type === 'goal' : /\bgoal\b/i.test(k.label) && !/goal kick|goal-kick/i.test(k.label));
}

export function goalCoverage(summary: ResultSummary): { covered: number; goals: number } {
  const g = goalMoments(summary.report);
  const goals = Math.max(summary.score.home + summary.score.away, g.length);
  return { covered: Math.min(goals, g.filter((k) => k.covered).length), goals };
}

export function recordMatch(save: SaveData, summary: ResultSummary, opts: StartMatchOptions, extras: MatchExtras = {}): RecordOutcome {
  const r = summary.report;
  const peak = Math.max(extras.peakViewers ?? 0, r.viewers);
  const out: RecordOutcome = { isNewDailyBest: false, granted: [] };

  save.stats.matchesPlayed += 1;
  save.stats.bestRating = Math.max(save.stats.bestRating, r.rating);
  save.stats.bestStreak = Math.max(save.stats.bestStreak, extras.bestStreak ?? r.streak);
  save.stats.peakViewers = Math.max(save.stats.peakViewers, peak);
  save.stats.totalPoints += Math.max(0, Math.round(r.total));

  if (opts.mode === 'daily' && opts.daily) {
    const gc = goalCoverage(summary);
    const res = recordDaily(save.daily, {
      dateKey: opts.daily.dateKey, dailyNumber: opts.daily.number || dailyNumber(opts.daily.dateKey), seed: summary.seed,
      rating: Math.round(r.rating), letter: r.letter, total: Math.round(r.total), peakViewers: peak,
      goalsCovered: gc.covered, goals: gc.goals, playedAt: summary.playedAt,
    });
    save.daily = res.bests;
    out.isNewDailyBest = res.isNewBest;
  }

  if (opts.mode === 'career') {
    const plan = nextCareerMatch(save.career);
    // Only count the result if it was the match the career expected (retrying an old seed doesn't advance).
    if (plan.seed === summary.seed) {
      const res = recordCareerResult(save.career, { seed: summary.seed, rating: r.rating, letter: r.letter, total: r.total, peakViewers: peak });
      save.career = res.state;
      const g = grantUnlocks(save.unlocks, res.rewards);
      save.unlocks = g.unlocked;
      out.granted = g.granted;
      const nextTier = res.outcome === 'promoted' ? nextCareerMatch(res.state).tier.name : undefined;
      out.career = { outcome: res.outcome, average: res.average, tierName: res.tier.name, nextTierName: nextTier };
    }
  }
  return out;
}
